import { Record, List, Map as ImmutableMap, OrderedMap } from 'immutable';
import * as A from './actions';
import { GridApplyTransactionFactory } from '../documents/factories';
import { PaymentFactory } from './factories';
import type { PaymentsStateData } from './types';

const initialState = Record<PaymentsStateData>({
  list: OrderedMap(),
  gridHeadersList: List(),
  gridColumns: null,
  gridDefaultColumns: null,
  gridPaymentsByPageIndex: ImmutableMap(),
  gridSearchAfterByPageIndex: ImmutableMap(),
  gridLoader: false,
  gridFilters: null,
  gridSorting: null,
  gridRefreshServerSide: null,
  gridApplyTransaction: GridApplyTransactionFactory(),
  loaded: false,
  count: 0,
  totalByState: ImmutableMap(),
  payment: PaymentFactory(),
  navigation: ImmutableMap(),
  cachedPayments: List(),
});

export const reducer = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payments(state = initialState(), action: any) {
    switch (action.type) {
      case A.paymentsGridGetHeadersAction.type:
        return state.set('gridLoader', true);

      case A.paymentsGridGetHeadersAction.success:
        return state.set('gridHeadersList', action.payload).set('gridLoader', false);

      case A.paymentsGridGetHeadersAction.failure:
        return state.set('gridLoader', false);

      case A.paymentsGridGetRowsAction.success:
        return state
          .set('list', action.payload.list)
          .set('count', action.payload.count)
          .set('gridLoader', false)
          .set('loaded', true);

      case A.paymentsGridFilterAppliedAction.type:
        return state.set('gridFilters', action.payload.filters);

      case A.paymentsGridSortingAppliedAction.type:
        return state.set('gridSorting', action.payload.sorting);

      case A.paymentsGridPaymentsByPageIndexAction.type:
        return action.payload.purge
          ? state.set('gridPaymentsByPageIndex', action.payload.data)
          : state.update('gridPaymentsByPageIndex', (prevMap) => prevMap.merge(action.payload.data));

      case A.paymentsGridSearchAfterByPageIndexAction.type:
        return action.payload.purge
          ? state.set('gridSearchAfterByPageIndex', action.payload.data)
          : state.update('gridSearchAfterByPageIndex', (prevMap) => prevMap.merge(action.payload.data));

      case A.paymentsGridColumnsAppliedAction.type:
        return state.set('gridColumns', action.payload);

      case A.paymentsGridDefaultColumnsAppliedAction.type:
        return state.set('gridDefaultColumns', action.payload);

      case A.paymentsGridClearConfigurationChangesAction.type:
        return state.set('gridColumns', null).set('gridSorting', null).set('gridFilters', null);

      case A.paymentsGridApplyTransactionAction.type:
        return state.set('gridApplyTransaction', GridApplyTransactionFactory(action.payload));

      // If true, then all rows at the level getting refreshed are immediately destroyed and 'loading' rows will appear.
      // If false, then all rows at the level getting refreshed are kept until rows are loaded (no 'loading' rows appear).
      // null - initial/default state
      case A.paymentsGridRefreshServerSideAction.type:
        return state.set('gridRefreshServerSide', action.payload);

      case A.paymentsGridGetTotalByStateAction.type:
        return state.set('totalByState', action.payload);

      case A.paymentsGridClearTotalByStateAction.type:
        return state.set('totalByState', ImmutableMap());

      case A.paymentGetAction.success:
        return state.set('payment', action.payload);

      case A.clearPaymentAction.type:
        return state.set('payment', PaymentFactory());

      case A.paymentUpdateInfoAction.success:
        return state
          .setIn(['payment', 'info'], action.payload.info)
          .setIn(['payment', 'invoices'], action.payload.invoices)
          .setIn(['payment', 'balance'], action.payload.balance)
          .setIn(['payment', 'meta'], action.payload.meta);

      case A.paymentDeleteBeneficiaryAction.success:
        return state.setIn(['payment', 'info'], action.payload.info).setIn(['payment', 'meta'], action.payload.meta);

      case A.payPaymentAction.success:
      case A.voidPaymentAction.success:
        return state.setIn(['payment', 'info'], action.payload.info);

      case A.updatePaymentTagsAction.success:
        return state.setIn(['payment', 'tags'], action.payload.tags);

      case A.paymentUpdateInvoicesAction.type:
        return state.setIn(['payment', 'invoices', action.payload.rowIndex], action.payload.invoice);

      case A.paymentUpdateInvoicesAction.success:
        return state
          .setIn(['payment', 'invoices'], action.payload.invoices)
          .setIn(['payment', 'info'], action.payload.info)
          .setIn(['payment', 'approvals'], action.payload.approvals);

      case A.paymentUpdateInvoicesRollbackAction.type:
        return state.setIn(['payment', 'invoices'], action.payload.invoices);

      case A.startPaymentApprovalFlowAction.success:
        return state
          .setIn(['payment', 'info'], action.payload.info)
          .setIn(['payment', 'approvals'], action.payload.approvals)
          .setIn(['payment', 'invoices'], action.payload.invoices)
          .setIn(['payment', 'tags'], action.payload.tags);

      case A.approvePaymentAction.success:
      case A.rejectPaymentAction.success:
      case A.resetPaymentProcessAction.success:
        return state
          .setIn(['payment', 'info'], action.payload.info)
          .setIn(['payment', 'approvals'], action.payload.approvals)
          .setIn(['payment', 'tags'], action.payload.tags);

      case A.recreatePaymentAction.success:
        return state.setIn(['payment', 'tags'], action.payload.tags);

      case A.unmergePaymentAction.success:
        return state
          .setIn(['payment', 'info'], action.payload.info)
          .setIn(['payment', 'approvals'], action.payload.approvals)
          .setIn(['payment', 'balance'], action.payload.balance)
          .setIn(['payment', 'invoices'], action.payload.invoices);

      case A.setPaymentNavigationAction.type:
        return state.set('navigation', ImmutableMap(action.payload));

      case A.updatePaymentNavigationAction.type:
        return state.update('navigation', (n) => n.merge(action.payload));

      case A.clearPaymentNavigationAction.type:
        return state.set('navigation', ImmutableMap());

      case A.saveCachedPaymentAction.type:
        return state.update('cachedPayments', (payments) => {
          const index = payments.findIndex(({ hash }) => hash === action.payload.data.hash);
          if (index !== -1) {
            return payments.set(index, action.payload.data);
          }
          if (payments.size === 50) {
            const oldest = payments.get(0);
            if (oldest?.url) window.URL.revokeObjectURL(oldest.url);
            return payments.delete(0).push(action.payload.data);
          }
          return payments.push(action.payload.data);
        });

      default:
        return state;
    }
  },
};
